/* <careers-filter> - Careers openings facet filter.
 *
 * Observed attributes: none.
 * Dispatched events:   none.
 * Consumed events:     none.
 *
 * Why client-side rather than the Section Rendering API pattern used by
 * <filter-pills> (Phase 6.5): Liquid cannot read arbitrary query-string
 * parameters, so there is no server-side facet path to swap to. The full role
 * list is server-rendered and this element narrows it in place. With the
 * element absent (JS off) the section's <noscript> block hides the controls and
 * the unfiltered list stays readable.
 *
 * Reads facet values from each role's data-country / data-city / data-area
 * attributes - the same attributes Liquid emits - so there is no parallel JSON
 * payload to drift out of sync.
 *
 * Cascading behavior (per the approved handoff):
 *   - Picking a country narrows the city <select> to cities that actually have
 *     openings in that country.
 *   - Career-area chips always stay visible; their counts recompute against the
 *     current country/city selection and areas that drop to zero dim out
 *     instead of disappearing.
 */

const SHEET_BREAKPOINT = 768;

class CareersFilter extends HTMLElement {
  connectedCallback() {
    this.state = { country: '', city: '', area: '' };

    this.list = this.querySelector('[data-roles-list]');
    this.roles = Array.from(this.querySelectorAll('[data-role]'));
    this.countrySelect = this.querySelector('[data-filter="country"]');
    this.citySelect = this.querySelector('[data-filter="city"]');
    this.chipWrap = this.querySelector('[data-area-chips]');
    this.areaSummary = this.querySelector('[data-area-summary]');
    this.noMatches = this.querySelector('[data-no-matches]');
    this.countEl = this.querySelector('[data-openings-count]')
      || document.querySelector('[data-section-id="page-open-roles"] [data-openings-count]');
    this.card = this.querySelector('[data-filter-card]');
    this.trigger = this.querySelector('[data-filter-open]');
    this.badge = this.querySelector('[data-filter-badge]');

    // Remember where the filter card lives inline so it can be put back when
    // the viewport grows past the bottom-sheet breakpoint.
    if (this.card) {
      this.cardHome = this.card.parentNode;
      this.cardAnchor = this.card.nextSibling;
    }

    if (!this.list || !this.roles.length) return;

    // Snapshot every role's facets once; the DOM is the source of truth but we
    // avoid re-reading attributes on every keystroke.
    this.facets = this.roles.map((el) => ({
      el,
      country: el.dataset.country || '',
      city: el.dataset.city || '',
      area: el.dataset.area || '',
    }));

    // Remember the full city option list so we can restore it when the country
    // filter is cleared.
    if (this.citySelect) {
      this.allCityOptions = Array.from(this.citySelect.options).map((o) => ({
        value: o.value,
        label: o.textContent,
      }));
    }

    this._onCountryChange = this._onCountryChange.bind(this);
    this._onCityChange = this._onCityChange.bind(this);
    this._onChipClick = this._onChipClick.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onSheetClick = this._onSheetClick.bind(this);
    this._onViewportChange = this._onViewportChange.bind(this);

    if (this.countrySelect) this.countrySelect.addEventListener('change', this._onCountryChange);
    if (this.citySelect) this.citySelect.addEventListener('change', this._onCityChange);
    if (this.chipWrap) this.chipWrap.addEventListener('click', this._onChipClick);
    if (this.trigger) this.trigger.addEventListener('click', this._onTriggerClick);

    this.mq = window.matchMedia(`(min-width: ${SHEET_BREAKPOINT}px)`);
    this.mq.addEventListener('change', this._onViewportChange);
    this._onViewportChange();

    this._apply();
  }

  disconnectedCallback() {
    if (this.countrySelect) this.countrySelect.removeEventListener('change', this._onCountryChange);
    if (this.citySelect) this.citySelect.removeEventListener('change', this._onCityChange);
    if (this.chipWrap) this.chipWrap.removeEventListener('click', this._onChipClick);
    if (this.trigger) this.trigger.removeEventListener('click', this._onTriggerClick);
    if (this.mq) this.mq.removeEventListener('change', this._onViewportChange);
    if (this.sheet) this.sheet.removeEventListener('click', this._onSheetClick);
  }

  /* ---------- events ---------- */

  _onCountryChange() {
    this.state.country = this.countrySelect.value;
    // A city outside the newly chosen country is no longer reachable.
    if (this.state.city && !this._citiesFor(this.state.country).includes(this.state.city)) {
      this.state.city = '';
      if (this.citySelect) this.citySelect.value = '';
    }
    this._syncCityOptions();
    this._apply();
  }

  _onCityChange() {
    this.state.city = this.citySelect.value;
    this._apply();
  }

  _onChipClick(event) {
    const chip = event.target.closest('[data-area]');
    if (!chip || chip.disabled) return;
    this.state.area = chip.dataset.area || '';
    this._apply();
  }

  _onTriggerClick() {
    const sheet = this._ensureSheet();
    if (sheet && typeof sheet.showModal === 'function') sheet.showModal();
  }

  _onSheetClick(event) {
    if (event.target.closest('[data-sheet-close]')) {
      this.sheet.close();
      return;
    }
    if (event.target.closest('[data-sheet-clear]')) {
      this.state = { country: '', city: '', area: '' };
      if (this.countrySelect) this.countrySelect.value = '';
      if (this.citySelect) this.citySelect.value = '';
      this._syncCityOptions();
      this._apply();
    }
  }

  _onViewportChange() {
    const isDesktop = this.mq.matches;
    if (this.trigger) this.trigger.hidden = isDesktop;
    if (!isDesktop) return;
    // Back to desktop: close the sheet and return the card to its inline slot,
    // otherwise it stays orphaned inside the closed <dialog> and disappears.
    if (this.sheet && this.sheet.open) this.sheet.close();
    if (this.card && this.cardHome && this.card.parentNode !== this.cardHome) {
      this.cardHome.insertBefore(this.card, this.cardAnchor);
    }
  }

  /* ---------- mobile bottom sheet ---------- */

  _ensureSheet() {
    if (this.sheet) {
      this._sheetBody.appendChild(this.card);
      return this.sheet;
    }
    if (!this.card) return null;

    const sheet = document.createElement('dialog');
    sheet.className = 'page-open-roles__sheet';
    sheet.innerHTML = [
      '<div class="page-open-roles__sheet-inner">',
      '<span class="page-open-roles__sheet-grip" aria-hidden="true"></span>',
      '<div class="page-open-roles__sheet-head">',
      `<h3 class="page-open-roles__sheet-title">${this._label('filterRoles')}</h3>`,
      `<button type="button" class="page-open-roles__sheet-clear" data-sheet-clear>${this._label('clear')}</button>`,
      '</div>',
      '<div class="page-open-roles__sheet-body" data-sheet-body></div>',
      '<button type="button" class="btn btn--primary btn--lg page-open-roles__sheet-apply" data-sheet-close></button>',
      '</div>',
    ].join('');

    this.appendChild(sheet);
    this.sheet = sheet;
    this._sheetBody = sheet.querySelector('[data-sheet-body]');
    this._sheetApply = sheet.querySelector('[data-sheet-close]');
    sheet.addEventListener('click', this._onSheetClick);

    this._sheetBody.appendChild(this.card);
    this._syncSheetApplyLabel();
    return sheet;
  }

  /* Labels are read off the rendered DOM rather than hardcoded, so the section
     keeps a single source of truth in locales/en.default.json. */
  _label(kind) {
    if (kind === 'filterRoles') {
      const t = this.trigger && this.trigger.querySelector('span');
      return t ? t.textContent : '';
    }
    if (kind === 'clear') return this.dataset.clearLabel || '';
    return '';
  }

  _syncSheetApplyLabel() {
    if (!this._sheetApply || !this.countEl) return;
    this._sheetApply.textContent = this.countEl.textContent.trim();
  }

  /* ---------- filtering ---------- */

  _citiesFor(country) {
    const out = [];
    this.facets.forEach((f) => {
      if (!f.city) return;
      if (country && f.country !== country) return;
      if (!out.includes(f.city)) out.push(f.city);
    });
    return out;
  }

  /* Rebuild the city <select> so it only offers cities with real openings in
     the chosen country. */
  _syncCityOptions() {
    if (!this.citySelect || !this.allCityOptions) return;
    const reachable = this._citiesFor(this.state.country);
    const current = this.citySelect.value;
    this.citySelect.textContent = '';
    this.allCityOptions.forEach((opt) => {
      if (opt.value !== '' && !reachable.includes(opt.value)) return;
      const el = document.createElement('option');
      el.value = opt.value;
      el.textContent = opt.label;
      this.citySelect.appendChild(el);
    });
    this.citySelect.value = reachable.includes(current) ? current : '';
  }

  _matches(f, ignore) {
    if (ignore !== 'country' && this.state.country && f.country !== this.state.country) return false;
    if (ignore !== 'city' && this.state.city && f.city !== this.state.city) return false;
    if (ignore !== 'area' && this.state.area && f.area !== this.state.area) return false;
    return true;
  }

  _apply() {
    let visible = 0;
    this.facets.forEach((f) => {
      const show = this._matches(f);
      f.el.hidden = !show;
      if (show) visible += 1;
    });

    // Area chip counts are computed ignoring the area facet itself, so each
    // chip answers "how many would I get if I picked this?".
    if (this.chipWrap) {
      Array.from(this.chipWrap.querySelectorAll('[data-area]')).forEach((chip) => {
        const area = chip.dataset.area || '';
        const isActive = area === this.state.area;
        chip.classList.toggle('is-active', isActive);
        chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');

        const countEl = chip.querySelector('.page-open-roles__chip-count');
        if (!countEl) return;
        const n = area === ''
          ? this.facets.filter((f) => this._matches(f, 'area')).length
          : this.facets.filter((f) => f.area === area && this._matches(f, 'area')).length;
        countEl.textContent = String(n);
        // Never disable the active chip - the visitor needs a way back out.
        const dim = n === 0 && !isActive;
        chip.classList.toggle('page-open-roles__chip--empty', dim);
        chip.disabled = dim;
      });
    }

    if (this.areaSummary) {
      const activeChipLabel = this.chipWrap && this.chipWrap.querySelector('.is-active span');
      this.areaSummary.textContent = activeChipLabel ? activeChipLabel.textContent : '';
    }

    if (this.noMatches) this.noMatches.hidden = visible > 0;
    if (this.list) this.list.hidden = visible === 0;

    if (this.countEl) {
      // Templates come from `'page.careers.openings_count' | t: count: 1|999`,
      // so Shopify has already picked the singular/plural wording and
      // interpolated a number. Swap the leading digits for the live count -
      // same approach as assets/... archive-collections-grid's formatCount.
      const tpl = visible === 1
        ? this.countEl.dataset.templateOne
        : this.countEl.dataset.templateOther;
      if (tpl) this.countEl.textContent = tpl.replace(/^\d+/, String(visible));
    }

    const activeFacetCount = ['country', 'city', 'area'].filter((k) => this.state[k]).length;
    if (this.badge) {
      this.badge.hidden = activeFacetCount === 0;
      this.badge.textContent = String(activeFacetCount);
    }
    this._syncSheetApplyLabel();
  }
}

if (!customElements.get('careers-filter')) {
  customElements.define('careers-filter', CareersFilter);
}
